import * as React from "react";
import { Grid, Box, Typography } from "@mui/material";
import { useNavigate } from "react-router-dom";
import { useDispatch } from "react-redux";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import Button from "@mui/material/Button";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import {
  apiSlice,
  useGetDeclinedJobCardsQuery,
  useEditRedoInstallMutation,
  useAddChangeLogMutation,
} from "../api/apiSlice";
import { showError } from "../../Global/notifications/notificationsSlice";
import { setsteps, setCurrentStep } from "./Stepper/stepperSlice";
import { Nav } from "../QE/Nav/Elements";
import { jobType } from "../jobType";

function TopNav() {
  return (
    <Box sx={{ mb: 10 }}>
      <Nav style={{ background: "#12131A", borderBottom: "1px solid rgba(255,255,255,0.06)" }}>
        <br />
        <Grid container alignItems="center" spacing={2}>
          <Grid item xs={2} align="center" sx={{ ml: 2 }}>
            <Backbutton />
          </Grid>
          <Grid item xs={8} align="left" sx={{ color: "#fff", fontWeight: 600 }}>
            Alerts
          </Grid>
        </Grid>
      </Nav>
    </Box>
  );
}

export default function Alerts() {
  const { data: job_card_data, isSuccess: isGetJobCardDeclinedSuccess } =
    useGetDeclinedJobCardsQuery();

  if (isGetJobCardDeclinedSuccess) {
    return (
      <Grid container alignItems="center" justifyContent="flex-start" sx={{ mt: 10 }}>
        <TopNav />
        <DeclinedList data={job_card_data.job_cards} />
      </Grid>
    );
  } else return null;
}

// item is the card as the redo endpoint returned it, so its state is the step to open.
function resumeToStep(item, navigate, dispatch) {
  const targetState = item.state;
  const ref = item.reference_id;
  const sn = item.serial_number || "";
  const imsi = item.imsi_number || "";
  const archSn = item.archived_serial_number || "";
  const archImsi = item.archived_imsi_number || "";

  if (item.install_state === "NEW") {
    dispatch(setsteps([0, 1, 2, 3, 4, 5]));
    if (targetState === "QRC") {
      dispatch(setCurrentStep(1));
      navigate(`/installer/${ref}/qrcode/`);
    } else if (targetState === "BEFWA" || targetState === "BEF") {
      dispatch(setCurrentStep(2));
      navigate(`/installer/${ref}/${sn}/${imsi}/work_area_before_install/`);
    } else if (targetState === "AMP") {
      dispatch(setCurrentStep(3));
      navigate(`/installer/${ref}/${sn}/${imsi}/connection_test_interlude/`);
    } else if (targetState === "TES") {
      dispatch(setCurrentStep(4));
      navigate(`/installer/${ref}/${sn}/${imsi}/connection_test/`);
    } else if (targetState === "AFT" || targetState === "AFTWA") {
      dispatch(setCurrentStep(5));
      navigate(`/installer/${ref}/${sn}/${imsi}/after_install/`);
    } else {
      navigate("/installer/");
    }
  } else if (item.install_state === "UN") {
    dispatch(setsteps([0, 1, 2, 3]));
    if (targetState === "BEFWA" || targetState === "BEF") {
      dispatch(setCurrentStep(1));
      navigate(`/installer/${ref}/${archSn}/${archImsi}/work_area_before_install/`);
    } else if (targetState === "UNI") {
      dispatch(setCurrentStep(2));
      navigate(`/installer/${ref}/${archSn}/${archImsi}/UN/`);
    } else if (targetState === "AFT" || targetState === "AFTWA") {
      dispatch(setCurrentStep(3));
      navigate(`/installer/${ref}/${archSn}/${archImsi}/after_install/`);
    } else {
      navigate("/installer/");
    }
  } else if (item.install_state === "RE") {
    dispatch(setsteps([0, 1, 2, 3, 4, 5, 6, 7]));
    if (targetState === "BEFWA" || targetState === "BEF") {
      dispatch(setCurrentStep(1));
      navigate(`/installer/${ref}/${archSn}/${archImsi}/work_area_before_install/`);
    } else if (targetState === "UNI") {
      dispatch(setCurrentStep(2));
      navigate(`/installer/${ref}/${archSn}/${archImsi}/RE/`);
    } else if (targetState === "QRCRE") {
      dispatch(setCurrentStep(3));
      navigate(`/installer/${ref}/qrcode/replacement/`);
    } else if (targetState === "DET") {
      dispatch(setCurrentStep(4));
      navigate(`/installer/${ref}/${sn}/${imsi}/details/`);
    } else if (targetState === "AMP") {
      dispatch(setCurrentStep(5));
      navigate(`/installer/${ref}/${sn}/${imsi}/connection_test_interlude/`);
    } else if (targetState === "TES") {
      dispatch(setCurrentStep(6));
      navigate(`/installer/${ref}/${sn}/${imsi}/connection_test/`);
    } else if (targetState === "AFT" || targetState === "AFTWA") {
      dispatch(setCurrentStep(7));
      navigate(`/installer/${ref}/${sn}/${imsi}/after_install/`);
    } else {
      navigate("/installer/");
    }
  } else {
    navigate("/installer/");
  }
}

const when = (iso) =>
  new Date(iso).toLocaleString("en-ZA", { day: "numeric", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" });

function DeclinedList({ data }) {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const [starting, setStarting] = React.useState(false);
  const [redoInstall] = useEditRedoInstallMutation();
  const [addChangeLog] = useAddChangeLogMutation();

  // QE marked this install REMOVE: it does not proceed and the device comes off through
  // a normal uninstall (scan, photos, QE approval), which then closes this card.
  const handleStartRemoval = () => {
    dispatch(setCurrentStep(0));
    dispatch(setsteps([0, 1, 2, 3]));
    navigate("/installer/uninstall/UN/");
  };

  // The redo starts first and the step comes from the server's card, not this list's copy:
  // a stale list sent a reset redo to the decline's old restart point (TF3). The history
  // entry follows only a redo that actually started, so a refused one or a repeat tap
  // logs nothing; it used to be written first and gate the redo.
  const handleReinstall = async (item) => {
    if (starting) return;
    setStarting(true);
    let result;
    try {
      result = await redoInstall({ body: { job_card_id: item.id } }).unwrap();
    } catch (error) {
      // A refused redo (e.g. the device has since been uninstalled) did nothing on screen.
      // Say why, and reload the list so a job that no longer applies drops off it.
      setStarting(false);
      dispatch(showError(error?.data?.error || "This job can no longer be redone."));
      dispatch(apiSlice.util.invalidateTags(["redo"]));
      return;
    }
    const card = result.job_cards;
    if (!result.already_resumed) {
      addChangeLog({
        body: {
          serial_number: card.install_state !== "UN" ? card.serial_number : card.archived_serial_number,
          change:
            card.install_state !== "UN"
              ? "Installer is redoing declined install"
              : "Installer is redoing declined uninstall",
        },
      });
    }
    resumeToStep(card, navigate, dispatch);
  };

  if (data.length === 0) {
    return (
      <Grid container alignItems="center" justifyContent="center" sx={{ mt: 5 }}>
        <Typography sx={{ fontSize: 20, color: "rgba(255,255,255,0.5)" }}>
          No alerts
        </Typography>
      </Grid>
    );
  }

  return data.map((item) => (
    <React.Fragment key={item.id}>
      <Grid item xs={0.5} />
      <Grid item xs={11}>
        <Box
          sx={{
            backgroundColor: "#161B22",
            border: "1px solid rgba(255,255,255,0.07)",
            borderRadius: "12px",
            p: 2,
            mb: 1.5,
          }}
        >
          {/* Header row */}
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              mb: 1.5,
            }}
          >
            {/* A redo the installer stopped is theirs to restart, not a new QE decline (D9). */}
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <WarningAmberIcon sx={{ color: item.redo_stopped_at ? "#FDB940" : "#ef4444", fontSize: 18 }} />
              <Typography sx={{ color: item.redo_stopped_at ? "#FDB940" : "#ef4444", fontSize: "0.85rem", fontWeight: 700 }}>
                {item.redo_stopped_at
                  ? "Redo stopped · start again"
                  : item.qe_decline_outcome === "REMOVE"
                    ? "Install Declined · Remove Device"
                    : `${jobType(item.install_state).noun} Declined`}
              </Typography>
            </Box>
            <Typography
              sx={{
                backgroundColor: "rgba(255,255,255,0.06)",
                color: "rgba(255,255,255,0.55)",
                fontSize: "0.7rem",
                px: 1,
                py: 0.4,
                borderRadius: "6px",
                fontFamily: "monospace",
              }}
            >
              {item.reference_id || `JC-${item.id}`}
            </Typography>
          </Box>

          {item.redo_stopped_at && (
            <Typography sx={{ fontSize: "0.82rem", color: "rgba(255,255,255,0.7)", mb: 1.5 }}>
              You stopped this redo on {when(item.redo_stopped_at)}. Starting again begins at the before
              photos; the client details are kept.
            </Typography>
          )}

          {/* Job start */}
          <Box sx={{ mb: 1 }}>
            <Typography
              sx={{
                fontSize: "0.62rem",
                color: "rgba(255,255,255,0.35)",
                textTransform: "uppercase",
                letterSpacing: "0.08em",
                mb: 0.25,
              }}
            >
              Job started
            </Typography>
            <Typography sx={{ fontSize: "0.9rem", color: "#fff" }}>
              {item.installation_date ? when(item.installation_date) : "—"}
            </Typography>
          </Box>

          {/* Address */}
          <Box sx={{ mb: 1 }}>
            <Typography
              sx={{
                fontSize: "0.62rem",
                color: "rgba(255,255,255,0.35)",
                textTransform: "uppercase",
                letterSpacing: "0.08em",
                mb: 0.25,
              }}
            >
              Address
            </Typography>
            <Typography sx={{ fontSize: "0.9rem", color: "#fff" }}>
              {item.address}
            </Typography>
          </Box>

          {/* Reason */}
          <Box sx={{ mb: 1.5 }}>
            <Typography
              sx={{
                fontSize: "0.62rem",
                color: "rgba(255,255,255,0.35)",
                textTransform: "uppercase",
                letterSpacing: "0.08em",
                mb: 0.25,
              }}
            >
              QE's reason
            </Typography>
            <Typography sx={{ fontSize: "0.9rem", color: "#fff" }}>
              {item.qe_decline_reason}
            </Typography>
          </Box>

          {item.qe_decline_outcome === "REMOVE" && (
            <Typography sx={{ fontSize: "0.82rem", color: "rgba(255,255,255,0.7)", mb: 1.5 }}>
              QE asked for this device to be removed, not reinstalled. Take it off the wall and
              complete the removal so the device goes back into stock.
            </Typography>
          )}

          <Box sx={{ display: "flex", justifyContent: "flex-end" }}>
            <Button
              onClick={() => (item.qe_decline_outcome === "REMOVE" ? handleStartRemoval() : handleReinstall(item))}
              disabled={starting}
              variant="contained"
              sx={{
                backgroundColor: "#FDB940",
                color: "#000",
                fontWeight: 700,
                borderRadius: "8px",
                px: 2.5,
                "&:hover": { backgroundColor: "#e6a832" },
              }}
            >
              {item.qe_decline_outcome === "REMOVE" ? "Start removal" : "Start redo"}
            </Button>
          </Box>
        </Box>
      </Grid>
      <Grid item xs={0.5} />
    </React.Fragment>
  ));
}

function Backbutton() {
  const navigate = useNavigate();
  return (
    <ArrowBackIcon
      onClick={() => navigate(-1)}
      sx={{ color: "#fff", cursor: "pointer" }}
    />
  );
}
